// pricing/pricingData.ts
export type BillingPeriod = "monthly" | "annual";
export type PlanKey = "free" | "pro" | "ultimate" | "enterprise";
type LimitedPlanKey = Exclude<PlanKey, "enterprise">;

export interface PlanFeatureBullet {
  label: string;
  highlight?: boolean;
}

export interface PlanFeatureBullets {
  inheritLabel?: string;
  bullets: PlanFeatureBullet[];
}

export interface PricingPlan {
  id: PlanKey;
  pricingId: {
    monthly: string;
    annual: string;
  };
  name: string;
  description: string;
  priceMonthly: number;
  priceAnnual: number;
  popular: boolean;
  cta: string;
  features?: string[];
  isCustom?: boolean;
  image?: string;
  featureBullets?: PlanFeatureBullets;
}

export interface FeatureRow {
  label: string;
  values: Record<PlanKey, string | boolean>;
}

type PricingTranslate = (
  key: string,
  values?: Record<string, string | number>,
) => string;

/** Single source of truth for plan limits — table & card bullets derive from here. */
export const PRICING_LIMITS = {
  coins: { free: 500, pro: 18_000, ultimate: 60_000 },
  talentPool: { free: 100, pro: 1_000, ultimate: 10_000 },
  jobs: { free: 10, pro: 100 },
  templates: { free: 1, pro: 50 },
  screenings: { free: 2 },
  communitySearchPerDay: { free: 3, pro: 10, ultimate: 30 },
  resumeScanningPerDay: { free: 30, pro: 200, ultimate: 500 },
  atsGenerate: { free: 0 },
} as const;

const cdnUrl = process.env.NEXT_PUBLIC_CLOUDFRONT_CDN_URL;

function formatPricingCount(value: number): string {
  return value.toLocaleString("en-US");
}

function perDayLimit(t: PricingTranslate, count: number): string {
  return t("features.perDay", { count: formatPricingCount(count) });
}

function coinsBullet(t: PricingTranslate, plan: LimitedPlanKey): string {
  return t("bullets.templates.coins", {
    count: formatPricingCount(PRICING_LIMITS.coins[plan]),
  });
}

function talentPoolBullet(t: PricingTranslate, plan: LimitedPlanKey): string {
  return t("bullets.templates.talentPool", {
    count: formatPricingCount(PRICING_LIMITS.talentPool[plan]),
  });
}

function jobsBullet(t: PricingTranslate, plan: "free" | "pro"): string {
  return t("bullets.templates.jobs", {
    count: formatPricingCount(PRICING_LIMITS.jobs[plan]),
  });
}

function templatesBullet(t: PricingTranslate, plan: "free" | "pro"): string {
  const count = PRICING_LIMITS.templates[plan];
  const key =
    count === 1
      ? "bullets.templates.templates_one"
      : "bullets.templates.templates_other";
  return t(key, { count: formatPricingCount(count) });
}

function screeningsBullet(t: PricingTranslate, plan: "free" | "pro"): string {
  if (plan === "free") {
    return t("bullets.templates.screenings", {
      count: formatPricingCount(PRICING_LIMITS.screenings.free),
    });
  }
  return t("bullets.templates.screeningsUnlimited");
}

function communitySearchBullet(
  t: PricingTranslate,
  plan: LimitedPlanKey,
): string {
  return t("bullets.templates.communitySearch", {
    limit: perDayLimit(t, PRICING_LIMITS.communitySearchPerDay[plan]),
  });
}

function resumeScanningBullet(
  t: PricingTranslate,
  plan: LimitedPlanKey,
): string {
  return t("bullets.templates.resumeScanning", {
    limit: perDayLimit(t, PRICING_LIMITS.resumeScanningPerDay[plan]),
  });
}

function atsGenerateBullet(t: PricingTranslate, plan: "free" | "pro"): string {
  if (plan === "free") {
    return t("bullets.templates.atsGenerateNone");
  }
  return t("bullets.templates.atsGenerateUnlimited");
}

export const getPricingProduct = () => {
  const dev = {
    pro: {
      monthly: "pri_01kptchxdyng02n3v731vxyhhz",
      annual: "pri_01kptkbeg0wsxwgp5pss6fs7c1",
    },
    ultimate: {
      monthly: "pri_01kptv28j168rr75v4hjq7nb2f",
      annual: "pri_01kptv3bc46qjeyvgckxdsbcbs",
    },
  };
  const prod = {
    pro: {
      monthly: "pri_01kwrfyqfep96fe5pgdes4cwat",
      annual: "pri_01kwrfy5a6hwvjxf0st45tjtzg",
    },
    ultimate: {
      monthly: "pri_01kwrfwx1ay0yw55nr01xd8ceh",
      annual: "pri_01kwrfvptpx9pznmnjbmxc40k3",
    },
  };
  return process.env.NEXT_PUBLIC_ENV === "prod" ? prod : dev;
};

export const getPricingPlans = (t: PricingTranslate): PricingPlan[] => [
  {
    id: "free",
    name: t("plans.free.name"),
    description: t("plans.free.description"),
    priceMonthly: 0,
    priceAnnual: 0,
    popular: false,
    cta: t("plans.free.cta"),
    pricingId: {
      monthly: "",
      annual: "",
    },
    image: `${cdnUrl}/images/started.png`,
  },
  {
    id: "pro",
    name: t("plans.pro.name"),
    description: t("plans.pro.description"),
    priceMonthly: 20,
    priceAnnual: 16,
    popular: false,
    cta: t("plans.pro.cta"),
    image: `${cdnUrl}/images/pro-plan.png`,
    pricingId: getPricingProduct().pro,
  },
  {
    id: "ultimate",
    name: t("plans.ultimate.name"),
    description: t("plans.ultimate.description"),
    priceMonthly: 50,
    priceAnnual: 40,
    popular: true,
    cta: t("plans.ultimate.cta"),
    image: `${cdnUrl}/images/ultra-plan.png`,
    pricingId: getPricingProduct().ultimate,
  },
  {
    id: "enterprise",
    name: t("plans.enterprise.name"),
    description: t("plans.enterprise.description"),
    priceMonthly: 0,
    priceAnnual: 0,
    popular: false,
    cta: t("plans.enterprise.cta"),
    isCustom: true,
    image: `${cdnUrl}/images/enterprise.png`,
    pricingId: {
      monthly: "",
      annual: "",
    },
  },
];

export const getFeatureRows = (t: PricingTranslate): FeatureRow[] => [
  {
    label: t("features.coinPerMonth.label"),
    values: {
      free: formatPricingCount(PRICING_LIMITS.coins.free),
      pro: formatPricingCount(PRICING_LIMITS.coins.pro),
      ultimate: formatPricingCount(PRICING_LIMITS.coins.ultimate),
      enterprise: t("common.custom"),
    },
  },
  {
    label: t("features.totalCvInTalentPool.label"),
    values: {
      free: formatPricingCount(PRICING_LIMITS.talentPool.free),
      pro: formatPricingCount(PRICING_LIMITS.talentPool.pro),
      ultimate: formatPricingCount(PRICING_LIMITS.talentPool.ultimate),
      enterprise: t("common.custom"),
    },
  },
  {
    label: t("features.jobCount.label"),
    values: {
      free: formatPricingCount(PRICING_LIMITS.jobs.free),
      pro: formatPricingCount(PRICING_LIMITS.jobs.pro),
      ultimate: t("common.unlimited"),
      enterprise: t("common.unlimited"),
    },
  },
  {
    label: t("features.templateCount.label"),
    values: {
      free: formatPricingCount(PRICING_LIMITS.templates.free),
      pro: formatPricingCount(PRICING_LIMITS.templates.pro),
      ultimate: t("common.unlimited"),
      enterprise: t("common.unlimited"),
    },
  },
  {
    label: t("features.screeningCount.label"),
    values: {
      free: formatPricingCount(PRICING_LIMITS.screenings.free),
      pro: t("common.unlimited"),
      ultimate: t("common.unlimited"),
      enterprise: t("common.unlimited"),
    },
  },
  {
    label: t("features.communitySearchPerDay.label"),
    values: {
      free: perDayLimit(t, PRICING_LIMITS.communitySearchPerDay.free),
      pro: perDayLimit(t, PRICING_LIMITS.communitySearchPerDay.pro),
      ultimate: perDayLimit(t, PRICING_LIMITS.communitySearchPerDay.ultimate),
      enterprise: t("common.unlimited"),
    },
  },
  {
    label: t("features.atsGenerate.label"),
    values: {
      free: String(PRICING_LIMITS.atsGenerate.free),
      pro: t("common.unlimited"),
      ultimate: t("common.unlimited"),
      enterprise: t("common.unlimited"),
    },
  },
  {
    label: t("features.resumeScanningPerDay.label"),
    values: {
      free: perDayLimit(t, PRICING_LIMITS.resumeScanningPerDay.free),
      pro: perDayLimit(t, PRICING_LIMITS.resumeScanningPerDay.pro),
      ultimate: perDayLimit(t, PRICING_LIMITS.resumeScanningPerDay.ultimate),
      enterprise: t("common.custom"),
    },
  },
  {
    label: t("features.removeAppAds.label"),
    values: { free: false, pro: true, ultimate: true, enterprise: true },
  },
];

export const getPlanFeatureBullets = (
  t: PricingTranslate,
): Record<PlanKey, PlanFeatureBullets> => ({
  free: {
    bullets: [
      { label: coinsBullet(t, "free") },
      { label: talentPoolBullet(t, "free") },
      { label: jobsBullet(t, "free") },
      { label: templatesBullet(t, "free") },
      { label: screeningsBullet(t, "free") },
      { label: communitySearchBullet(t, "free") },
      { label: resumeScanningBullet(t, "free") },
      { label: atsGenerateBullet(t, "free") },
    ],
  },

  pro: {
    inheritLabel: t("bullets.inheritLabel.free"),
    bullets: [
      { label: coinsBullet(t, "pro"), highlight: true },
      { label: talentPoolBullet(t, "pro") },
      { label: jobsBullet(t, "pro") },
      { label: templatesBullet(t, "pro") },
      { label: screeningsBullet(t, "pro") },
      { label: communitySearchBullet(t, "pro") },
      { label: resumeScanningBullet(t, "pro") },
      { label: atsGenerateBullet(t, "pro") },
      { label: t("bullets.templates.noAds") },
    ],
  },

  ultimate: {
    inheritLabel: t("bullets.inheritLabel.pro"),
    bullets: [
      { label: coinsBullet(t, "ultimate"), highlight: true },
      { label: talentPoolBullet(t, "ultimate") },
      { label: t("bullets.templates.jobsUnlimited") },
      { label: t("bullets.templates.templatesUnlimited") },
      { label: communitySearchBullet(t, "ultimate") },
      { label: resumeScanningBullet(t, "ultimate") },
    ],
  },

  enterprise: {
    inheritLabel: t("bullets.inheritLabel.ultimate"),
    bullets: [
      { label: t("bullets.enterprise.coins") },
      { label: t("bullets.enterprise.talentPool") },
      { label: t("bullets.enterprise.resumeScanning") },
      { label: t("bullets.enterprise.accountManager"), highlight: true },
      { label: t("bullets.enterprise.sla") },
      { label: t("bullets.enterprise.sso") },
      { label: t("bullets.enterprise.support") },
    ],
  },
});
